import React from "react";

import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  Modal,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import UploadButtonBeforeImage from "../../assets/upload-button-before.png";
import UploadButtonAfterImage from "../../assets/upload-button-after.png";
import ExtractButtonImage from "../../assets/detail-extract-button.png";
import { ReactComponent as SquareLeft } from "../../assets/square-left.svg";
import { ReactComponent as SquareRight } from "../../assets/square-right.svg";
import ConfirmationModal from "../../components/ConfirmationModal";
import { GradientBorderBox } from "./components/shared";
import StyledProgressBar from "./components/StyledProgressBar";
import { FlowState } from "./types";
import {
  steps,
} from "./constants";
import { StyledImageButton } from "./components/styled";
import { useUploadPage } from "./useUploadPage";
import { BorderedStepIcon } from "./components/BorderdStepIcon";
import { FileListItem } from "./components/FileListItem";
import { VerificationSection } from "./components/VerificationSection";
import { EditableGeneralDataTable } from "./components/EditableGeneralDataTable";
import { RentRollTable } from "./components/RentRollTable";
import { DetailedCheckerView } from "./components/DetailedCheckerView";

interface UploadPageProps {
  flowState: FlowState;
  setFlowState: (state: FlowState) => void;
  handleNavigate: (path: string) => void;
}

const UploadPage: React.FC<UploadPageProps> = ({
  flowState,
  setFlowState,
  handleNavigate,
}) => {
  // Hook を使用して状態管理を分離
  const {
    loading,
    error,
    snackbar,
    setSnackbar,
    fileLists,
    generalContracts,
    rentRollData,
    verificationResult,
    setVerificationResult,
    finalContracts,
    setFinalContracts,
    aggregatedContracts,
    setAggregatedContracts,
    unmatchedContractIds,
    setUnmatchedContractIds,
    completedFilesForProgressBar,
    totalFilesForProgressBar,
    showSummaryModal,
    setShowSummaryModal,
    setIsSummaryDataReady,
    setFailedUploads,
    allFailedFilesForSummary,
    setAllFailedFilesForSummary,
    rentRollPropertyFilter,
    setRentRollPropertyFilter,
    rentRollRoomFilter,
    setRentRollRoomFilter,
    rentRollLesseeFilter,
    setRentRollLesseeFilter,
    showResetConfirm,
    setShowResetConfirm,
    showFailedContractsDialog,
    setShowFailedContractsDialog,
    setFilesWereDeleted,
    selectedContract,
    detailScrollRef,
    handleSelectContract,
    confirmedContracts,
    setConfirmedContracts,
    needsReviewContracts,
    setNeedsReviewContracts,
    conflictResolutions,
    buildingChecks,
    detailedJobIds,
    detailedJobStatuses,
    handleBuildingResolutionChange,
    handleOwnerResolutionChange,
    filteredRentRollData,
    propertyOptions,
    roomOptions,
    lesseeOptions,
    getRootProps,
    getInputProps,
    isDragActive,
    handleRemoveFile,
    resetState,
    handleExtractGeneral,
    handleExtractDetails,
    handleSaveFinalData,
    handleGeneralContractChange,
    handleDeleteTmpRecord,
    handleDetailChange,
    handleConfirmAndNext,
    handleMarkForReview,
    handlePrev,
    handleNext,
    getActiveStep,
  } = useUploadPage(flowState, setFlowState, handleNavigate);


  // File drop and UI effects are now handled by separate hooks

  const handleProceedToDetails = () => {
    setShowSummaryModal(false);
    setIsSummaryDataReady(false);
    setFlowState("generalExtracted");
  };

  const handleAddMoreFiles = () => {
    // Reset summary-related states for the next upload
    setIsSummaryDataReady(false);
    setShowSummaryModal(false);
    setAllFailedFilesForSummary([]);
    setFailedUploads([]);
    setVerificationResult(null);
    setFilesWereDeleted(false); // Reset deletion flag when leaving the screen
    setFlowState("initial");
  };

  const missingDocsCount = verificationResult
    ? verificationResult.missing_documents.reduce(
        (acc, item) => acc + item.missing_documents.length,
        0,
      )
    : 0;
  const needsCorrectionCount = verificationResult
    ? verificationResult.unmatched_contracts.length
    : 0;

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#ECEBF0",
        borderRadius: "16px",
        boxShadow: `4px 4px 6px 0 rgba(26, 26, 26, 0.25),
                    -10px -10px 6px 0 rgba(255, 255, 255, 0.95)`,
        backdropFilter: "blur(4px)",
        border: "1px solid rgba(31, 31, 31, 0.05)",
        width: flowState === "detailedExtracted" ? "90%" : "80%", // Dynamically set width
        maxWidth: "1200px", // 最大幅を設定
        display: "flex",
        flexDirection: "column",
        mt: "2rem",
        ml: "0%",
        mb: "2rem",
        position: "relative",
        overflowY: "auto",
        overflowX: "hidden",
        maxHeight: "calc(100vh - 10rem)",
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontSize: "200%", fontWeight: "bold", color: "#FF7292" }}
      >
        新規データアップロード
      </Typography>
      <Stepper
        activeStep={getActiveStep}
        sx={{
          mb: 4,
          "& .MuiStepConnector-line": {
            borderTopStyle: "dotted",
            borderTopWidth: "2px",
            borderColor: "#FF7292",
          },
          "& .MuiStepLabel-label": {
            fontSize: "80%",
          },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel StepIconComponent={BorderedStepIcon}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {flowState === "processing" && (
        <StyledProgressBar
          key={
            flowState === "processing"
              ? "processing-progress-bar"
              : "initial-progress-bar"
          }
          value={
            totalFilesForProgressBar > 0
              ? (completedFilesForProgressBar / totalFilesForProgressBar) * 100
              : 0
          }
          labelText={`AIが書類を読んでいます... (${completedFilesForProgressBar} / ${totalFilesForProgressBar} 件処理中)`}
        />
      )}

      {flowState === "detailedProcessing" && (
        <StyledProgressBar
          value={
            detailedJobIds.length > 0
              ? (Object.values(detailedJobStatuses).filter(
                  (s) => s.status === "complete",
                ).length /
                  detailedJobIds.length) *
                100
              : 0
          }
          labelText={`AIが詳細情報を抽出しています... (${
            Object.values(detailedJobStatuses).filter(
              (s) => s.status === "complete",
            ).length
          } / ${detailedJobIds.length} 件処理中)`}
        />
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={resetState}>
              やり直す
            </Button>
          }
        >
          <Box component="div" sx={{ whiteSpace: 'pre-line' }}>
            {error}
          </Box>
        </Alert>
      )}

      {flowState === "initial" && !loading && (
        <Box mb={-10}>
          {/* 1. 外側のピンクの点線を持つBox */}
          <Box
            sx={{
              border: "2px dotted #FF7292",
              borderRadius: "16px", // 角を少し丸める
              padding: "4px", // 内側の線との間に少し余白を作る
            }}
          >
            {/* 2. 内側の実線を持つPaper */}
            <Paper
              {...getRootProps()}
              sx={{
                border: "3px solid rgb(218, 213, 238)", // 内側の線をピンク系の薄い実線に
                borderRadius: "12px", // 外側の角丸より少し小さい
                padding: 4,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragActive
                  ? "rgba(255, 114, 146, 0.1)"
                  : "#fff", // 背景を透明に
                boxShadow: "none", // Paperデフォルトの影を消す
              }}
            >
              <input {...getInputProps()} />
              <UploadFileIcon sx={{ fontSize: 54, color: "#FF7292" }} />
              <Typography sx={{ fontWeight: "bold", color: "#424242" }}>
                レントロールと契約書のファイルをドラッグ＆ドロップするか、ここをクリックして選択
              </Typography>
              <Typography
                sx={{ color: "#616161", fontSize: "0.875rem", mt: 0.5 }}
              >
                対応形式：PDF / Word / Excel / CSV (1ファイルあたり最大32MB)
              </Typography>
            </Paper>
          </Box>
          {fileLists.valid.length + fileLists.invalid.length > 0 && (
            <Box mt={2}>
              <Typography variant="h6">
                選択中のファイル ({fileLists.valid.length}件):
              </Typography>
              <Box>
                <List dense>
                  {fileLists.valid.map((fileWithStatus) => (
                    <FileListItem
                      key={fileWithStatus.id}
                      fileWithStatus={fileWithStatus}
                      onRemove={handleRemoveFile}
                    />
                  ))}
                  {fileLists.invalid.map((fileWithStatus) => (
                    <FileListItem
                      key={fileWithStatus.id}
                      fileWithStatus={fileWithStatus}
                      onRemove={handleRemoveFile}
                    />
                  ))}
                </List>
              </Box>{" "}
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            {fileLists.valid.length === 0 ? (
              <img
                src={UploadButtonBeforeImage}
                alt="アップロードして基本情報を抽出する"
                style={{
                  cursor: "not-allowed",
                  width: "350px",
                  maxWidth: "100%",
                  height: "auto",
                }}
              />
            ) : (
              <StyledImageButton
                src={UploadButtonAfterImage}
                alt="アップロードして基本情報を抽出する"
                onClick={handleExtractGeneral}
                style={{
                  width: "350px",
                  maxWidth: "100%",
                  height: "auto",
                }}
              />
            )}
          </Box>
        </Box>
      )}

      {flowState === "generalExtracted" && !loading && (
        <Box mt={4}>
          {allFailedFilesForSummary.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              以下のファイルの処理に失敗しました。これらのファイルは抽出対象外となります。
              <List dense sx={{ maxHeight: 100, overflow: "auto" }}>
                {allFailedFilesForSummary.map((filename, index) => (
                  <ListItem key={index} sx={{ py: 0.5, px: 1 }}>
                    <ListItemText
                      primary={filename}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: "error",
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
              ステップ2: 全般データの確認・修正
            </Typography>
            <Tooltip
              title="現在の内容を維持したままファイルを追加アップロードまたは削除することができます"
              placement="top"
            >
              <Button
                variant="contained"
                onClick={handleAddMoreFiles}
                sx={{
                  pr: 4,
                  pl: 4,
                  pt: 1,
                  pb: 1,
                  color: "white", // Text color
                  backgroundColor: "rgb(64, 144, 255)", // Blue color
                  border: "4px solid rgba(240, 232, 249, 0.9)", // Border color
                  borderRadius: "25px",
                  boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  "&:hover": {
                    backgroundColor: "rgb(64,144, 255)", // Darker blue on hover
                    border: "4px solid rgba(240, 232, 249, 0.9)",
                  },
                }}
              >
                ファイル選択へ戻る
              </Button>
            </Tooltip>
          </Box>
          {rentRollData.length > 0 ? (
            <Alert
              severity="info"
              sx={{
                mb: 2,
                backgroundColor: "rgb(64, 144, 255)",
                color: "white",
                "& .MuiAlert-icon": {
                  color: "white", // テキストとの間隔（お好みで）
                },
              }}
            >
              AIによる抽出結果です。特に「物件名」「部屋名」「入居者名」が正しいか確認・修正してください。赤色の行はレントロールと一致しないものです。
            </Alert>
          ) : (
            <Alert
              severity="info"
              sx={{
                mb: 2,
                backgroundColor: "rgb(64, 144, 255)",
                color: "white",
                "& .MuiAlert-icon": {
                  color: "white", // テキストとの間隔（お好みで）
                },
              }}
            >
              AIによる抽出結果です。内容を確認・修正してください。特に「物件名」「部屋名」「入居者名」が正しいか確認・修正してください。赤色の行はレントロールと一致しないものです。
            </Alert>
          )}
          {verificationResult && (
            <GradientBorderBox sx={{ mb: 2 }}>
              {" "}
              {/* Wrapped VerificationSection */}
              <VerificationSection verification={verificationResult} />
            </GradientBorderBox>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            抽出された基本データ（編集可能）
          </Typography>
          <GradientBorderBox sx={{ mb: 2 }}>
            {" "}
            {/* Wrapped EditableGeneralDataTable */}
            <EditableGeneralDataTable
              contracts={generalContracts}
              onContractChange={handleGeneralContractChange}
              unmatchedIds={unmatchedContractIds}
              onDeleteContract={handleDeleteTmpRecord}
            />
          </GradientBorderBox>
          {rentRollData.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                レントロールデータ
              </Typography>

              <GradientBorderBox sx={{ mb: 2 }}>
                {" "}
                {/* RentRollTable is now inside */}
                <RentRollTable
                  rentRolls={filteredRentRollData}
                  propertyFilter={rentRollPropertyFilter}
                  setRentRollPropertyFilter={setRentRollPropertyFilter}
                  roomFilter={rentRollRoomFilter}
                  setRentRollRoomFilter={setRentRollRoomFilter}
                  lesseeFilter={rentRollLesseeFilter}
                  setRentRollLesseeFilter={setRentRollLesseeFilter}
                  propertyOptions={propertyOptions}
                  roomOptions={roomOptions}
                  lesseeOptions={lesseeOptions}
                />
              </GradientBorderBox>
            </>
          )}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <StyledImageButton
              src={ExtractButtonImage}
              alt="詳細データを抽出する"
              onClick={handleExtractDetails}
              style={{
                width: "350px",
                maxWidth: "100%",
                height: "auto",
                marginRight: "20px", // Add some space between buttons
              }}
            />
          </Box>
        </Box>
      )}

      {flowState === "detailedExtracted" && !loading && (
        <Box mt={4}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
              ステップ3: 詳細データの確認・修正
            </Typography>
            <Button
              variant="contained" // Change variant to contained
              onClick={() => setShowResetConfirm(true)}
              sx={{
                // Copied from "DB閲覧画面へ" button
                pr: 3,
                pl: 3,
                pt: 0.5,
                pb: 0.5, // Reduced vertical padding
                color: "white",
                backgroundColor: "rgb(64, 144, 255)", // Blue color
                border: "4px solid rgba(240, 232, 249, 0.9)",
                borderRadius: "25px",
                boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
                fontWeight: "bold",
                fontSize: "1rem",
                "&:hover": {
                  backgroundColor: "rgb(64,144, 255)",
                  border: "4px solid rgba(240, 232, 249, 0.9)",
                  borderRadius: "25px",
                },
              }}
            >
              抽出へ戻る
            </Button>
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            AIによる詳細抽出結果です。最終確認し、必要に応じて修正してください。
          </Alert>
          <DetailedCheckerView
            aggregatedContracts={aggregatedContracts}
            finalContracts={finalContracts}
            selectedContract={selectedContract}
            onSelectContract={handleSelectContract}
            confirmedContracts={confirmedContracts}
            needsReviewContracts={needsReviewContracts}
            onDetailChange={handleDetailChange}
            handleConfirmAndNext={handleConfirmAndNext}
            handleMarkForReview={handleMarkForReview}
            handleSaveFinalData={handleSaveFinalData}
            setFlowState={setFlowState}
            handlePrev={handlePrev}
            handleNext={handleNext}
            flowState={flowState}
            detailScrollRef={detailScrollRef}
            buildingChecks={buildingChecks}
            conflictResolutions={conflictResolutions}
            handleBuildingResolutionChange={handleBuildingResolutionChange}
            handleOwnerResolutionChange={handleOwnerResolutionChange}
          />
        </Box>
      )}

      {flowState === "saved" && !loading && (
        <Box mt={4} textAlign="center">
          <Alert
            severity="success"
            sx={{ mb: 2, backgroundColor: "#ccffea", color: "black" }}
          >
            データベースへの保存が完了しました。
          </Alert>
          <Button
            variant="contained"
            onClick={() => handleNavigate("/confirm")}
            sx={{
              mr: 2,
              pr: 4,
              pl: 4,
              pt: 1,
              pb: 1,
              color: "white",
              backgroundColor: "rgb(64, 144, 255)", // Blue color
              border: "4px solid rgba(240, 232, 249, 0.9)",
              borderRadius: "25px", // Changed to 25px
              boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)", // Removed the "glow" part
              fontWeight: "bold",
              fontSize: "1rem",
              "&:hover": {
                backgroundColor: "rgb(64,144, 255)",
                border: "4px solid rgba(240, 232, 249, 0.9)", // Retain border on hover
                borderRadius: "25px", // Hover should also use 25px
              },
            }}
          >
            DB閲覧画面へ
          </Button>
          <Button
            variant="outlined" // Changed to outlined
            onClick={resetState}
            sx={{
              pr: 4,
              pl: 4,
              pt: 1,
              pb: 1,
              color: "rgba(255, 255, 255, 1)", // Blue text
              backgroundColor: "rgba(179, 175, 175, 1)",
              border: "4px solid rgba(240, 232, 249, 0.9)", // Blue border
              borderRadius: "25px",
              boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
              fontWeight: "bold",
              fontSize: "1rem",
              "&:hover": {
                backgroundColor: "rgba(206, 206, 206, 1)", // Darker gray for subtle hover
                border: "4px solid rgba(240, 232, 249, 0.9)",
                borderRadius: "25px",
              },
            }}
          >
            続けて他のファイルをアップロード
          </Button>
        </Box>
      )}

      <Modal
        key={showSummaryModal ? "summary-modal-open" : "summary-modal-closed"}
        open={
          showSummaryModal &&
          !(
            flowState === "processing" &&
            completedFilesForProgressBar < totalFilesForProgressBar
          )
        }
        onClose={handleProceedToDetails}
        aria-labelledby="summary-modal-title"
      >
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            p: 4, // Padding of 16px
            borderRadius: "12px",
            outline: "none", // Prevent flicker
            // Using ::before pseudo-element for the inner border
            "::before": {
              content: '""',
              position: "absolute",
              inset: "4px",
              border: "2px solid rgba(115, 169, 244, 1)",
              pointerEvents: "none",
              borderRadius: "12px", // Match parent radius
            },
          }}
        >
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Box
              sx={{
                pb: 1, // Padding bottom for spacing between title and line
                mb: 2, // Margin bottom for spacing below the line
                borderBottom: "2px solid rgba(115, 169, 244, 1)", // Blue line
                width: "calc(100% - 2px)", // Adjust width to leave 12px padding on each side
                mx: "auto", // Center the line
              }}
            >
              <Typography
                id="summary-modal-title"
                variant="h6"
                component="h2"
                gutterBottom
                sx={{ textAlign: "center", color: "rgba(115, 169, 244, 1)" }} // Center the text itself and set color
              >
                一次処理 結果概要
              </Typography>
            </Box>
            {rentRollData.length === 0 && (
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body1" component="span">
                    ・レントロールデータ:
                  </Typography>
                  <Typography variant="body1" component="span">
                    <strong>なし</strong>
                  </Typography>
                </Box>
              </Box>
            )}
            {verificationResult && (
              <Box sx={{ mt: rentRollData.length === 0 ? 1 : 2 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="body1" component="span">
                    ・不足書類:
                  </Typography>
                  <Typography variant="body1" component="span">
                    <strong>{missingDocsCount}</strong> 枚
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body1" component="span">
                    ・レントロールと不一致:
                  </Typography>
                  <Typography variant="body1" component="span">
                    <strong>{needsCorrectionCount}</strong> 枚
                  </Typography>
                </Box>
              </Box>
            )}
            {allFailedFilesForSummary.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  color="error"
                >
                  <Typography variant="body1" component="span">
                    ・処理失敗ファイル:
                  </Typography>
                  <Typography variant="body1" component="span">
                    <strong>{allFailedFilesForSummary.length}</strong> 件
                  </Typography>
                </Box>
                <List dense sx={{ maxHeight: 100, overflow: "auto", mt: 1 }}>
                  {allFailedFilesForSummary.map((filename, index) => (
                    <ListItem key={index} sx={{ py: 0.5, px: 1 }}>
                      <ListItemText
                        primary={filename}
                        primaryTypographyProps={{
                          variant: "body2",
                          color: "error",
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </CardContent>
          <CardActions sx={{ justifyContent: "center", p: 0, pt: 2 }}>
            <Button
              onClick={handleProceedToDetails}
              variant="contained"
              sx={{
                // Copied and adapted from ConfirmPage button
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                pr: 4,
                pl: 4,
                pt: 1,
                pb: 1,
                color: "white",
                backgroundColor: "rgb(64, 144, 255)", // Blue color
                border: "4px solid rgba(240, 232, 249, 0.9)",
                borderRadius: "50px",
                boxShadow:
                  "-6px -6px 5px rgb(255, 255, 255), 4px 4px 20px rgba(0, 0, 0, 0.2)",
                fontWeight: "bold", // Make text bold
                fontSize: "1rem", // Set font size
                "&:hover": {
                  backgroundColor: "rgb(64,144, 255)",
                  border: "4px solid rgba(240, 232, 249, 0.9)",
                  borderRadius: "50px",
                },
              }}
            >
              詳細を確認・修正する
            </Button>
          </CardActions>
        </Card>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity || "info"}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* stickyコンテナ */}
      <Box
        sx={{
          display: "flex",
          pointerEvents: "none", // コンテナ自体はクリックイベントを妨げない
        }}
      >
        <Box sx={{ width: "9%", pointerEvents: "auto" }}>
          <SquareLeft />
        </Box>
        <Box sx={{ width: "9%", pointerEvents: "auto", marginLeft: "auto" }}>
          <SquareRight />
        </Box>
      </Box>

      <ConfirmationModal
        open={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={() => {
          setFlowState("generalExtracted");
          setConfirmedContracts(new Set());
          setNeedsReviewContracts(new Set());
          // Clear detailed extraction caches
          // setDbCheckedContracts(new Set());
          // setChangedContractIds(new Set());
          setFinalContracts([]);
          setAggregatedContracts([]);
          setUnmatchedContractIds(new Set());
        }}
        title="詳細抽出のやり直し確認"
        message="前の画面に戻ると、再度詳細データの抽出からやり直しになります。よろしいですか？"
        confirmButtonText="やり直す"
        themeColor="warning"
      />

      {/* 保存失敗した契約の詳細ダイアログ */}
      <Dialog
        open={showFailedContractsDialog}
        onClose={() => setShowFailedContractsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: "#ffebee", color: "#c62828" }}>
          保存に失敗した契約
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            以下の契約の保存に失敗しました。
            <strong>
              一時的なエラーでない場合、システムエラーの可能性があります。
            </strong>
            問題が続く場合は、システム管理者に問い合わせてください。
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>No.</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>ファイル名</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>物件名</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>部屋番号</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>エラー内容</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {([] as any[]).map((failedContract, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{failedContract.index + 1}</TableCell>
                    <TableCell>
                      {failedContract.filename
                        ? failedContract.filename.split(/[/／]/).pop()
                        : "-"}
                    </TableCell>
                    <TableCell>{failedContract.property_name || "-"}</TableCell>
                    <TableCell>{failedContract.room_name || "-"}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="error">
                        {failedContract.error_message}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowFailedContractsDialog(false)}
            variant="contained"
            color="primary"
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};




export default UploadPage;
